<template>
    <div class="w-full px-4 py-8 space-y-10">
        <!-- Header -->
        <div class="text-center fade-in">
            <h1 class="text-3xl font-bold text-indigo-700">Work Readiness Dashboard</h1>
            <p class="text-gray-500">Track your progress, earn badges, and climb the leaderboard</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 fade-in">
            <!-- Badge & Progress Section -->

            <div v-if="isBadgeLoading"
                class="bg-white rounded-2xl shadow-xl p-6 md:p-8 flex flex-col items-center space-y-6 animate-pulse">
                <!-- Badge Placeholder -->
                <div class="text-center space-y-3">
                    <div class="w-20 h-20 rounded-full bg-gray-200 mx-auto"></div>
                    <div class="w-48 h-6 bg-gray-200 mx-auto rounded"></div>
                    <div class="w-32 h-4 bg-gray-200 mx-auto rounded"></div>
                    <div class="w-40 h-4 bg-gray-300 mx-auto rounded"></div>
                </div>
                <!-- Progress Placeholder -->
                <div class="w-full max-w-xl space-y-3">
                    <div class="flex justify-between">
                        <div class="w-16 h-3 bg-gray-200 rounded"></div>
                        <div class="w-24 h-3 bg-gray-200 rounded"></div>
                    </div>

                    <div class="w-full bg-gray-200 h-4 rounded-full overflow-hidden">
                        <div class="h-full bg-gray-300 w-1/2 rounded-full"></div>
                    </div>

                    <div class="w-12 h-3 bg-gray-200 ml-auto rounded"></div>

                    <div class="w-64 h-4 bg-gray-100 mx-auto rounded"></div>
                </div>
            </div>
            <div v-else-if="errorMsg"
                class="text-red-500 bg-white rounded-2xl shadow-xl p-6 md:p-8 flex flex-col items-center justify-center space-y-6 animate-pulse">
                {{ errorMsg }}</div>
            <div v-else class="bg-white rounded-2xl shadow-xl p-6 md:p-8 flex flex-col items-center space-y-6">
                <!-- Badge Display -->
                <div class="text-center space-y-2">
                    <div v-if="currentBadge.image_url && false" class="flex justify-center">
                        <img :src="currentBadge.image_url" class="w-20 h-20" alt="badge" />
                    </div>
                    <div v-else class="text-6xl">{{ currentBadge.emoji }}</div>

                    <h2 class="text-2xl font-bold text-gray-800">
                        {{ currentBadge.name }} <span class="text-indigo-600">{{ currentBadge.title }}</span>
                    </h2>
                    <p class="text-gray-500 text-sm">Badge Level: 1 / 5</p>

                    <p class="text-indigo-700 font-semibold text-sm md:text-base">
                        🎯 Your Score: <span class="text-lg font-bold">{{ leaderboard.points }}</span> pts
                    </p>
                </div>

                <!-- Progress Section -->
                <div class="w-full max-w-xl">
                    <!-- Points Header -->
                    <div class="flex justify-between text-xs md:text-sm text-gray-600 mb-1 font-medium">
                        <span>{{ leaderboard.points }} pts</span>
                        <span v-if="nextBadge">
                            {{ nextBadge.points_min }} pts
                            <span class="hidden sm:inline">({{ nextBadge.name }} {{ nextBadge.title }})</span>
                        </span>
                    </div>

                    <!-- Progress Bar -->
                    <div class="relative w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div class="absolute left-0 top-0 h-full bg-gradient-to-r from-indigo-500 to-indigo-700 transition-all duration-500 rounded-full"
                            :style="{ width: progressPercentage + '%' }"></div>
                    </div>

                    <!-- Percentage Display -->
                    <div class="mt-1 text-right text-xs text-gray-500 font-medium">
                        {{ progressPercentage }}%
                    </div>

                    <!-- Next Badge Hint -->
                    <p v-if="nextBadge" class="mt-3 text-sm text-gray-500 text-center">
                        🚀 Only <span class="text-gray-700 font-semibold">
                            {{ nextBadge.points_min - leaderboard.points }} pts</span> to reach
                        <span class="text-indigo-600 font-semibold">
                            {{ nextBadge.name }} {{ nextBadge.title }}
                        </span>!
                    </p>
                </div>
            </div>


            <!-- Leaderboard -->
            <div class="bg-white p-6 rounded-xl shadow fade-in">
                <h3 class="text-lg font-bold text-yellow-600 mb-4">Leaderboard</h3>
                <table class="w-full text-left text-sm">
                    <thead>
                        <tr class="text-gray-500 border-b">
                            <th class="py-2">#</th>
                            <th class="py-2">Name</th>
                            <th class="py-2">Points</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="!$apollo.queries.leaderboardListWithMe.loading">
                            <tr v-for="(item, index) in leaderboardList" :key="index"
                                :class="['border-b', item.user.id === loggedInUserId ? 'bg-yellow-100 font-semibold' : '']">
                                <td class="py-2">{{ item.rank }}</td>
                                <td class="py-2">{{ item.user.id === loggedInUserId ? 'You' : item.user.name }}</td>
                                <td class="py-2">{{ item.points }}</td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr v-for="n in 3" :key="n">
                                <td colspan="3" class="py-2">
                                    <div class="h-4 w-full rounded animate-shimmer"></div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <div class="bg-white p-6 rounded-xl shadow-lg">
                <div class="flex items-center justify-between mb-6">
                    <h3 class="text-xl font-bold text-indigo-700 flex items-center">
                        <svg class="w-6 h-6 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2">
                            </path>
                        </svg>
                        Current Activities
                    </h3>
                </div>
                <div v-if="$apollo.queries.work_readiness_programs.loading" class="space-y-3">
                    <div v-for="n in 3" :key="n"
                        class="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-100 animate-shimmer h-32">
                    </div>
                </div>
                <div v-else-if="!hasCurrentActivities" class="text-center py-12">
                    <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
                        <svg class="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2">
                            </path>
                        </svg>
                    </div>
                    <p class="text-gray-600 font-medium">No current activities available</p>
                    <p class="text-sm text-gray-400 mt-1">Check back later for new opportunities</p>
                </div>
                <div v-else>
                    <ul class="space-y-4">
                        <li v-for="(activity, i) in currentActivities" :key="i"
                            class="bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-50 rounded-xl p-5 border border-indigo-100 hover:border-indigo-300 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                            <div class="flex justify-between items-start mb-4">
                                <div class="flex-1 pr-4">
                                    <div class="flex items-center mb-2">
                                        <div
                                            class="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center mr-3">
                                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253">
                                                </path>
                                            </svg>
                                        </div>
                                        <div class="flex-1">
                                            <h4 class="font-bold text-gray-800 text-lg leading-tight">{{ activity.title
                                            }}</h4>
                                        </div>
                                    </div>
                                    <p class="text-sm text-gray-600 leading-relaxed line-clamp-2 ml-13 mb-3">{{
                                        activity.description
                                    }}</p>
                                    <div v-if="activity.creator && activity.creator.name"
                                        class="flex items-center text-xs text-gray-500 ml-13">
                                        <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z">
                                            </path>
                                        </svg>
                                        <span>Created by <span class="font-semibold text-gray-700">{{
                                            activity.creator.name
                                        }}</span></span>
                                    </div>
                                </div>
                                <div
                                    class="flex-shrink-0 bg-white rounded-lg px-3 py-2 border-2 border-indigo-200 shadow-sm">
                                    <div class="text-center">
                                        <div class="text-xs text-gray-500 font-medium">Points</div>
                                        <div class="text-lg font-bold text-indigo-600">{{ activity.points }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="flex justify-end pt-3 border-t border-indigo-200">
                                <button v-if="isEnrolled(activity.token)"
                                    class="bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg px-6 py-2.5 text-sm shadow-md hover:shadow-lg transition-all duration-200 flex items-center space-x-2 cursor-not-allowed opacity-75">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M5 13l4 4L19 7">
                                        </path>
                                    </svg>
                                    <span>{{ isExternalActivity(activity) ? 'Registered' : 'Enrolled' }}</span>
                                </button>
                                <button v-else @click="goToUrl(activity.token)"
                                    class="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-lg px-6 py-2.5 text-sm shadow-md hover:shadow-lg transition-all duration-200 flex items-center space-x-2">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 4v16m8-8H4">
                                        </path>
                                    </svg>
                                    <span>{{ isExternalActivity(activity) ? 'Register Now' : 'Enroll Now' }}</span>
                                </button>
                            </div>
                        </li>
                </ul>
                    <div v-if="paginatorInfo && paginatorInfo.lastPage > 1" class="mt-6">
                        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                    </div>
                </div>
            </div>

            <div class="bg-white p-6 rounded-xl shadow-lg">
                <div class="flex items-center justify-between mb-6">
                    <h3 class="text-xl font-bold text-green-700 flex items-center">
                        <svg class="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        My Submissions
                    </h3>
                </div>
                <div v-if="$apollo.queries.myCompletedSubmissions.loading" class="space-y-3">
                    <div v-for="n in 3" :key="n"
                        class="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200 animate-shimmer h-24">
                    </div>
                </div>
                <div v-else-if="!hasCompletedActivities" class="text-center py-12">
                    <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                        <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                            </path>
                        </svg>
                    </div>
                    <p class="text-gray-600 font-medium">No submissions yet</p>
                    <p class="text-sm text-gray-400 mt-1">Start by enrolling in current activities</p>
                </div>
                <div v-else>
                    <ul class="space-y-3">
                        <li v-for="(submission, i) in completedActivities" :key="i"
                            @click="goToActivity(submission.activity.token)"
                            class="bg-white rounded-xl p-5 border-2 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                            :class="getSubmissionBorderClass(submission.status)">
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <div class="flex items-start mb-3">
                                        <div class="flex-shrink-0 mr-4"
                                            :class="getStatusIconBgClass(submission.status)">
                                            <svg class="w-6 h-6 p-2" :class="getStatusIconClass(submission.status)"
                                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    v-if="submission.status && (submission.status.toLowerCase() === 'approved' || submission.status.toLowerCase() === 'completed')"
                                                    stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                <path
                                                    v-else-if="submission.status && submission.status.toLowerCase() === 'pending'"
                                                    stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                <path v-else stroke-linecap="round" stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                                </path>
                                            </svg>
                                        </div>
                                        <div class="flex-1">
                                            <h4 class="font-bold text-gray-800 text-lg mb-2">{{
                                                submission.activity.title }}</h4>
                                            <div class="flex items-center flex-wrap gap-3">
                                                <div class="flex items-center text-xs text-gray-500">
                                                    <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                                                        </path>
                                                    </svg>
                                                    <span>Submitted on <span class="font-semibold text-gray-700">{{
                                                        formatDate(submission.created_at) }}</span></span>
                                                </div>
                                                <span class="text-xs px-3 py-1 rounded-full font-semibold shadow-sm"
                                                    :class="getStatusClass(submission.status)">
                                                    {{ formatStatus(submission.status) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="ml-4 flex-shrink-0">
                                    <div class="bg-gray-50 rounded-lg px-4 py-3 border-2 min-w-[80px] text-center"
                                        :class="getPointsBorderClass(submission.status)">
                                        <div class="text-xs text-gray-500 font-medium mb-1">Points</div>
                                        <div class="text-xl font-bold" :class="getPointsClass(submission.status)">
                                            {{ submission.points || submission.activity.points }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                </ul>
                    <div v-if="completedPaginatorInfo && completedPaginatorInfo.lastPage > 1" class="mt-6">
                        <pagination :paginatorInfo="completedPaginatorInfo" :setPage="setCompletedPage"></pagination>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import Pagination from "../../components/shared/pagination.vue";

const GET_LOGGED_IN_USER_LEADERBOARD = gql`
  query GetLoggedInUserLeaderboard {
    me {
      error
      message
      user {
        id
        name
        leaderboard {
          id
          points
          rank
          currentBadge {
            id
            name
            title
            emoji
            image_url
            points_min
            points_max
          }
          nextBadge {
            id
            name
            title
            emoji
            image_url
            points_min
            points_max
          }
        }
        workReadinessSubmissions {
            id
            status
            points
            created_at
            updated_at
            activity {
                token
                title
                points
            }
        }
      }
    }
  }
`;

const GET_LEADERBOARD_WITH_ME = gql`
  query LeaderboardListWithMe {
    leaderboardListWithMe {
      error
      message
      data {
        user {
            id
            name
        }
        points
        rank
      }
    }
  }
`;

const GET_MY_COMPLETED_SUBMISSIONS = gql`
  query GetMyCompletedSubmissions($first: Int!, $page: Int!) {
    getMyCompletedSubmissions(first: $first, page: $page) {
      error
      error_code
      message
      data {
        id
        status
        points
        created_at
        updated_at
        activity {
          token
          title
          points
        }
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

export default {
    components: {
        Pagination,
    },
    data() {
        return {
            isBadgeLoading: true,
            user: null,
            leaderboard: null,
            currentBadge: null,
            errorMsg: null,
            work_readiness_programs: [],
            enrolledActivities: [],
            leaderboardListWithMe: [],
            completedActivities: [],
            completedActivitiesPaginatorInfo: null,
            currentPage: 1,
            completedPage: 1,
            first: 10,
        };
    },
    mounted() {
        this.fetchUserLeaderboard();
    },
    computed: {
        progressPercentage() {
            if (!this.nextBadge) return 100;
            const progress = (this.leaderboard.points / this.nextBadge.points_min) * 100;
            return Math.min(100, Math.max(0, progress.toFixed(2)));
        },
        loggedInUserId() {
            return this.$store.getters.userId;
        },
        currentActivities() {
            return this.work_readiness_programs.data || [];
        },
        hasCurrentActivities() {
            return this.currentActivities && this.currentActivities.length > 0;
        },
        paginatorInfo() {
            return this.work_readiness_programs.paginatorInfo;
        },
        hasCompletedActivities() {
            return this.completedActivities && this.completedActivities.length > 0;
        },
        completedPaginatorInfo() {
            return this.completedActivitiesPaginatorInfo;
        },
        leaderboardList() {
            return this.leaderboardListWithMe.data;
        },
    },
    apollo: {
        work_readiness_programs() {
            return {
                query: gql`query GetAllWorkReadinessProgram($first: Int!, $page: Int!, $filter: String) {
                    work_readiness_programs(first: $first, page: $page, filter: $filter) {
                        error
                        error_code
                        message
                        data {
                            token
                            title
                            description
                            points
                            is_external
                            external_link
                            creator {
                                id
                                name
                            }
                        }
                        paginatorInfo {
                            count
                            currentPage
                            firstItem
                            hasMorePages
                            lastItem
                            lastPage
                            perPage
                            total
                        }
                    }
                }`,
                variables() {
                    return {
                        first: this.first,
                        page: this.currentPage,
                        filter: 'current',
                    }
                },
                fetchPolicy: 'cache-and-network'
            }
        },
        leaderboardListWithMe() {
            return {
                query: GET_LEADERBOARD_WITH_ME,
                fetchPolicy: 'cache-and-network'
            }
        },
        myCompletedSubmissions() {
            return {
                query: GET_MY_COMPLETED_SUBMISSIONS,
                variables() {
                    return {
                        first: this.first,
                        page: this.completedPage,
                    }
                },
                fetchPolicy: 'cache-and-network',
                update(data) {
                    if (data.getMyCompletedSubmissions && !data.getMyCompletedSubmissions.error) {
                        this.completedActivities = data.getMyCompletedSubmissions.data || [];
                        this.completedActivitiesPaginatorInfo = data.getMyCompletedSubmissions.paginatorInfo;
                    }
                    return data;
                }
            }
        },
    },
    methods: {
        async fetchUserLeaderboard() {
            try {
                this.isBadgeLoading = true;

                const { data } = await this.$apollo.query({
                    query: GET_LOGGED_IN_USER_LEADERBOARD,
                    fetchPolicy: 'network-only',
                });

                if (data.me.error) {
                    this.errorMsg = data.me.message || "Failed to fetch user.";
                    return;
                }

                const user = data.me.user;
                this.user = user;
                this.leaderboard = (user && user.leaderboard) ? user.leaderboard : null;
                this.currentBadge = user && user.leaderboard && user.leaderboard.currentBadge
                    ? user.leaderboard.currentBadge
                    : null;
                this.nextBadge = user && user.leaderboard && user.leaderboard.nextBadge
                    ? user.leaderboard.nextBadge
                    : null;
                this.enrolledActivities = user.workReadinessSubmissions.map(sub => sub.activity.token);

            } catch (error) {
                this.errorMsg = error.message || "Something went wrong.";
            } finally {
                this.isBadgeLoading = false;
            }
        },
        setPage(page) {
            this.currentPage = page;
        },
        setCompletedPage(page) {
            this.completedPage = page;
        },
        goToActivity(token) {
            if (token) {
                this.$router.push(`/my-work-readiness-program/${token}`);
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        },
        formatStatus(status) {
            if (!status) return 'Pending';
            return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        },
        getStatusClass(status) {
            if (!status || status.toLowerCase() === 'pending') {
                return 'bg-yellow-100 text-yellow-700';
            } else if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'completed') {
                return 'bg-green-100 text-green-700';
            } else if (status.toLowerCase() === 'rejected') {
                return 'bg-red-100 text-red-700';
            }
            return 'bg-gray-100 text-gray-700';
        },
        getPointsClass(status) {
            if (!status || status.toLowerCase() === 'pending') {
                return 'text-red-600';
            } else if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'completed') {
                return 'text-green-600';
            }
            return 'text-gray-600';
        },
        getSubmissionBorderClass(status) {
            if (!status || status.toLowerCase() === 'pending') {
                return 'border-yellow-200 bg-yellow-50';
            } else if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'completed') {
                return 'border-green-200 bg-green-50';
            } else if (status.toLowerCase() === 'rejected') {
                return 'border-red-200 bg-red-50';
            }
            return 'border-gray-200 bg-gray-50';
        },
        getPointsBorderClass(status) {
            if (!status || status.toLowerCase() === 'pending') {
                return 'border-red-200 bg-red-50';
            } else if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'completed') {
                return 'border-green-200 bg-green-50';
            }
            return 'border-gray-200';
        },
        getStatusIconBgClass(status) {
            if (!status || status.toLowerCase() === 'pending') {
                return 'bg-yellow-100 rounded-lg';
            } else if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'completed') {
                return 'bg-green-100 rounded-lg';
            } else if (status.toLowerCase() === 'rejected') {
                return 'bg-red-100 rounded-lg';
            }
            return 'bg-gray-100 rounded-lg';
        },
        getStatusIconClass(status) {
            if (!status || status.toLowerCase() === 'pending') {
                return 'text-yellow-600';
            } else if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'completed') {
                return 'text-green-600';
            } else if (status.toLowerCase() === 'rejected') {
                return 'text-red-600';
            }
            return 'text-gray-600';
        },
        goToUrl(url) {
            this.$router.replace(`/my-work-readiness-program/${url}`);
        },
        isEnrolled(token) {
            if (this.enrolledActivities.includes(token)) {
                return true;
            } else {
                return false;
            }
        },
        isExternalActivity(activity) {
            return activity && activity.is_external === true && activity.external_link;
        }
    },
};
</script>

<style lang="scss" scoped>
/* Tailwind required classes for shimmer */
@keyframes shimmer {
    0% {
        background-position: -500px 0;
    }

    100% {
        background-position: 500px 0;
    }
}

.animate-shimmer {
    background: linear-gradient(to right, #f0f0f0 8%, #e0e0e0 18%, #f0f0f0 33%);
    background-size: 1000px 100%;
    animation: shimmer 1.2s infinite linear;
}

.fade-in {
    animation: fade 0.6s ease-out forwards;
    opacity: 0;
}

@keyframes fade {
    0% {
        opacity: 0;
        transform: translateY(10px);
    }

    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

.truncate-4 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
}
</style>
